import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle, Clock,
  Eye, ArrowUpRight,
  X, ChevronDown, ChevronUp, Camera, Timer,
  Lightbulb,
} from 'lucide-react';
import api from '../services/api';
import { formatPrice } from '../utils/formatters';

// --- Onboarding tip definitions ---
const ONBOARDING_TIPS = {
  vitrine_not_configured: {
    id: 'vitrine_not_configured',
    title: 'Activez votre vitrine pour suivre l\'intérêt',
    description: 'Configurez votre vitrine publique pour voir quels véhicules attirent le plus de clics et de contacts.',
    action: { label: 'Configurer la vitrine', to: '/settings?tab=vitrine' },
  },
  first_week: {
    id: 'first_week',
    title: 'Vos insights se construisent',
    description: 'Les données de rotation et comparaisons mensuelles deviendront disponibles après quelques semaines d\'utilisation. En attendant, gérez votre stock et observez les premières tendances.',
    action: null,
  },
  no_vehicles: {
    id: 'no_vehicles',
    title: 'Ajoutez vos véhicules pour commencer',
    description: 'Importez vos véhicules depuis AutoScout24 ou ajoutez-les manuellement pour commencer à suivre votre stock.',
    action: { label: 'Ajouter un véhicule', to: '/vehicles/add' },
  },
};

// --- Small helper components ---
const AlertCard = ({ alert }) => {
  const severityStyles = {
    high: 'border-red-500/30 bg-red-500/5',
    medium: 'border-amber-500/30 bg-amber-500/5',
    low: 'border-blue-500/30 bg-blue-500/5',
  };
  const severityDot = {
    high: 'bg-red-400',
    medium: 'bg-amber-400',
    low: 'bg-blue-400',
  };
  const typeIcons = {
    dormant: Clock,
    slow_preparation: Timer,
    incomplete: Camera,
    no_interest: Eye,
  };
  const AlertIcon = typeIcons[alert.type] || AlertTriangle;

  return (
    <Link
      to={`/vehicles/${alert.vehicle_id}`}
      className={`flex items-center gap-3 p-3 rounded-lg border transition-colors hover:border-gray-600 ${severityStyles[alert.severity]}`}
    >
      <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-dark-hover overflow-hidden">
        {alert.vehicle_image ? (
          <img src={alert.vehicle_image} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <AlertIcon className="h-4 w-4 text-muted" />
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-primary truncate">{alert.vehicle_name}</p>
        <div className="flex items-center gap-2 text-xs">
          <span className={`inline-block w-1.5 h-1.5 rounded-full ${severityDot[alert.severity]}`} />
          <span className="text-secondary">{alert.message}</span>
        </div>
      </div>
      {alert.price > 0 && (
        <span className="text-xs font-medium text-secondary flex-shrink-0">{formatPrice(alert.price)}</span>
      )}
    </Link>
  );
};

const OnboardingTip = ({ tip, onDismiss }) => (
  <div className="relative bg-dark-card border border-carmin/20 rounded-xl p-4">
    <button
      onClick={() => onDismiss(tip.id)}
      className="absolute top-3 right-3 text-muted hover:text-primary transition-colors"
      title="Ne plus afficher"
    >
      <X className="h-4 w-4" />
    </button>
    <div className="flex items-start gap-3 pr-6">
      <div className="w-8 h-8 rounded-lg bg-carmin/10 flex items-center justify-center flex-shrink-0">
        <Lightbulb className="h-4 w-4 text-carmin" />
      </div>
      <div>
        <p className="text-sm font-medium text-primary">{tip.title}</p>
        <p className="text-xs text-secondary mt-1">{tip.description}</p>
        {tip.action && (
          <Link
            to={tip.action.to}
            className="inline-flex items-center gap-1 mt-2 text-xs font-medium text-carmin hover:text-carmin-light"
          >
            {tip.action.label}
            <ArrowUpRight className="h-3 w-3" />
          </Link>
        )}
      </div>
    </div>
  </div>
);

// --- Main component ---
const StockInsights = ({ vehicles, settings, onSettingsChange }) => {
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAlerts, setShowAlerts] = useState(true);
  const [dismissedTips, setDismissedTips] = useState(settings?.stock_dismissed_tips || []);

  const loadInsights = useCallback(async () => {
    try {
      const res = await api.get('/vehicles/insights');
      setInsights(res.data);
    } catch (err) {
      console.error('Error loading stock insights:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadInsights(); }, [loadInsights]);

  const dismissTip = useCallback(async (tipId) => {
    const newDismissed = [...dismissedTips, tipId];
    setDismissedTips(newDismissed);
    try {
      await api.put('/settings', { stock_dismissed_tips: newDismissed });
      if (onSettingsChange) onSettingsChange({ ...settings, stock_dismissed_tips: newDismissed });
    } catch (err) {
      // silent
    }
  }, [dismissedTips, settings, onSettingsChange]);

  if (loading) {
    return (
      <div className="mb-6 animate-pulse">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-dark-card rounded-xl border border-dark-border p-4 h-24" />
          ))}
        </div>
      </div>
    );
  }

  if (!insights) return null;

  const { kpis, alerts, interest, comparison, price_ranges, sell_duration } = insights;

  // --- Determine onboarding tips to show ---
  const tipsToShow = [];
  if (!vehicles || vehicles.length === 0) {
    if (!dismissedTips.includes('no_vehicles')) tipsToShow.push(ONBOARDING_TIPS.no_vehicles);
  } else {
    // Check if vitrine is configured (needed for interest data)
    const vitrineEnabled = settings?.vitrine_enabled && settings?.dealer_slug;
    if (!vitrineEnabled && !dismissedTips.includes('vitrine_not_configured')) {
      tipsToShow.push(ONBOARDING_TIPS.vitrine_not_configured);
    }
    // Check if first week (no comparison data yet)
    if (!comparison && !dismissedTips.includes('first_week') && vehicles.length > 0) {
      tipsToShow.push(ONBOARDING_TIPS.first_week);
    }
  }

  const SectionHeader = ({ icon: Icon, title, count, expanded, onToggle, color = 'text-secondary' }) => (
    <button
      onClick={onToggle}
      className="flex items-center justify-between w-full py-2 group"
    >
      <div className="flex items-center gap-2">
        <Icon className={`h-4 w-4 ${color}`} />
        <span className="text-sm font-medium text-primary">{title}</span>
        {count > 0 && (
          <span className="px-1.5 py-0.5 rounded-full bg-dark-hover text-xs text-secondary">{count}</span>
        )}
      </div>
      {expanded ? (
        <ChevronUp className="h-4 w-4 text-muted group-hover:text-primary" />
      ) : (
        <ChevronDown className="h-4 w-4 text-muted group-hover:text-primary" />
      )}
    </button>
  );

  return (
    <div className="mb-6 space-y-4">
      {/* Onboarding Tips */}
      {tipsToShow.length > 0 && (
        <div className="space-y-2">
          {tipsToShow.map(tip => (
            <OnboardingTip key={tip.id} tip={tip} onDismiss={dismissTip} />
          ))}
        </div>
      )}

      {/* Alerts Section */}
      {alerts.length > 0 && (
        <div className="bg-dark-card rounded-xl border border-dark-border p-4">
          <SectionHeader
            icon={AlertTriangle}
            title="Alertes"
            count={alerts.length}
            expanded={showAlerts}
            onToggle={() => setShowAlerts(!showAlerts)}
            color="text-amber-400"
          />
          {showAlerts && (
            <div className="space-y-2 mt-2">
              {alerts.slice(0, 8).map((alert, i) => (
                <AlertCard key={`${alert.vehicle_id}-${alert.type}-${i}`} alert={alert} />
              ))}
              {alerts.length > 8 && (
                <p className="text-xs text-muted text-center pt-1">
                  et {alerts.length - 8} autre{alerts.length - 8 > 1 ? 's' : ''} alerte{alerts.length - 8 > 1 ? 's' : ''}...
                </p>
              )}
            </div>
          )}
        </div>
      )}

    </div>
  );
};

export default StockInsights;
